import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  AlertTriangle, TrendingUp, TrendingDown, X as XIcon,
  Boxes, Package, ChevronDown, Warehouse,
} from "lucide-react";
import TabEcartsPro from "./KpiAchatsEcartsPro";
import DemarqueTab from "./achats/DemarqueTab";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import { InventaireInpulse } from "./achats/AchatsInpulse";

const MONTHS_FR = ["JANVIER","FEVRIER","MARS","AVRIL","MAI","JUIN","JUILLET","AOUT","SEPTEMBRE","OCTOBRE","NOVEMBRE","DECEMBRE"];
const fmtEur = v => (v==null||isNaN(v))?"—":Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")+" €";
const fmtInt = v => (v==null||isNaN(v))?"—":Math.round(v).toLocaleString("fr-FR").replace(/,/g," ");
const fmtNum = (v,d=2) => (v==null||isNaN(v))?"—":Number(v).toLocaleString("fr-FR",{maximumFractionDigits:d,minimumFractionDigits:d});
const fmtPct = (v,d=1) => (v==null||isNaN(v))?"—":`${(v*100).toFixed(d).replace(".",",")}%`;

/* ─── Multi-select ─── */
function MS({ label, options, value, onChange, testid, labelMap, search=true }) {
  return <FilterMultiSelect label={label} options={options} labelMap={labelMap} value={value||[]} onChange={onChange} testid={testid} size="md" topLabel className="flex-1 min-w-[140px]" placeholder={`Tous les ${label.toLowerCase()}`}/>;
}

/* ─── Card ─── */
function Card({ icon: Icon, label, value, sub, tone, alert, testid }) {
  const t = tone === "up" ? "text-emerald-600" : tone === "down" ? "text-rose-600" : "text-slate-500";
  return (
    <div data-testid={testid} className={`bg-white rounded-xl border ${alert?"border-rose-300 ring-2 ring-rose-100":"border-slate-200"} p-4 flex flex-col gap-1`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg grid place-items-center ${alert?"bg-rose-50 text-rose-600":"bg-blue-50 text-blue-600"}`}>
          {Icon && <Icon className="w-4 h-4"/>}
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      </div>
      <div className="text-[22px] font-bold text-slate-900 tabular-nums leading-tight">{value}</div>
      {sub && <div className={`text-[11px] ${t}`}>{sub}</div>}
    </div>
  );
}

/* ─── ViewToggle : Corner / DR / Secteur ─── */
function ViewToggle({ mode, setMode, testid }) {
  const opts = [
    { k: "corner",  label: "Vue Corner" },
    { k: "dr",      label: "Vue DR" },
    { k: "secteur", label: "Vue Secteur" },
  ];
  return (
    <div data-testid={testid} className="inline-flex items-center border border-slate-200 rounded overflow-hidden">
      {opts.map(o => (
        <button key={o.k} onClick={()=>setMode(o.k)}
                data-testid={`${testid}-${o.k}`}
                className={`px-2.5 py-1 text-[11px] font-semibold transition ${mode===o.k?"bg-blue-600 text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Barre de filtres indépendante par onglet ─── */
function TabFilterBar({ meta, f, setF, showProduit, testidPrefix }) {
  const drOpts = useMemo(()=>(meta?.drs||[]).slice().sort(),[meta]);
  const secteurOpts = useMemo(()=>(meta?.secteurs||[]).slice().sort(),[meta]);
  const enseigneOpts = useMemo(()=>(meta?.enseignes||[]).slice().sort(),[meta]);
  const corners = useMemo(()=>{
    const drs=f.drs||[], secteurs=f.secteurs||[], ens=f.enseignes||[]; const seen=new Set(); const keys=[], labels={};
    (meta?.corners||[]).forEach(c=>{
      if (drs.length && !drs.includes(c.dr)) return;
      if (secteurs.length && !secteurs.includes(c.secteur)) return;
      if (ens.length && !ens.includes(c.enseigne)) return;
      if (!c.corner_key || seen.has(c.corner_key)) return;
      seen.add(c.corner_key);
      keys.push(c.corner_key); labels[c.corner_key]=c.corner||c.corner_key;
    });
    keys.sort((a,b)=>(labels[a]||"").localeCompare(labels[b]||""));
    return { keys, labels };
  },[meta,f.drs,f.secteurs,f.enseignes]);
  const prodOpts = useMemo(()=>(meta?.produits||[]).slice().sort(),[meta]);
  const anOpts = useMemo(()=>(meta?.annees||[]).slice().sort((a,b)=>b-a).map(String),[meta]);
  const moOpts = useMemo(()=>(meta?.mois||[]).slice().sort((a,b)=>MONTHS_FR.indexOf(a)-MONTHS_FR.indexOf(b)),[meta]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-2" data-testid={`${testidPrefix}-filter-bar`}>
      <MS label="Enseigne" testid={`${testidPrefix}-f-enseigne`} options={enseigneOpts} value={f.enseignes||[]} onChange={v=>setF(p=>({...p,enseignes:v}))}/>
      <MS label="DR"      testid={`${testidPrefix}-f-dr`}      options={drOpts}      value={f.drs||[]}      onChange={v=>setF(p=>({...p,drs:v}))}/>
      <MS label="Secteur" testid={`${testidPrefix}-f-secteur`} options={secteurOpts} value={f.secteurs||[]} onChange={v=>setF(p=>({...p,secteurs:v}))}/>
      <MS label="Corner"  testid={`${testidPrefix}-f-corner`}  options={corners.keys} labelMap={corners.labels} value={f.corners||[]} onChange={v=>setF(p=>({...p,corners:v}))}/>
      {showProduit && <MS label="Produit" testid={`${testidPrefix}-f-produit`} options={prodOpts}    value={f.produits||[]} onChange={v=>setF(p=>({...p,produits:v}))}/>}
      <MS label="Année"   testid={`${testidPrefix}-f-annee`}   options={anOpts}      value={(f.annees||[]).map(String)} onChange={v=>setF(p=>({...p,annees:v.map(Number)}))}/>
      <MS label="Mois"    testid={`${testidPrefix}-f-mois`}    options={moOpts}      value={f.mois||[]}     onChange={v=>setF(p=>({...p,mois:v}))}/>
      {Object.values(f).some(a=>Array.isArray(a)&&a.length>0) && (
        <button onClick={()=>setF({drs:[],secteurs:[],enseignes:[],corners:[],produits:[],mois:[],annees:[]})}
                data-testid={`${testidPrefix}-f-reset`}
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 px-2 py-1">Réinitialiser</button>
      )}
    </div>
  );
}

const toQP = (f, extra={}) => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k,v]) => { if (Array.isArray(v) && v.length) p.append(k, v.join(",")); });
  Object.entries(extra).forEach(([k,v]) => { if (v !== undefined && v !== null && v !== "") p.append(k, String(v)); });
  return p.toString();
};

/* ─── Rendu cellule brute ─── */
function renderCell(col, val, isNumeric) {
  if (val === null || val === undefined || val === "") return <span className="text-slate-300">—</span>;
  if (isNumeric) {
    const lc = String(col).toLowerCase();
    if (lc.includes("€") || lc.includes("eur") || lc.includes("ca ht") || lc === "ca") return fmtEur(val);
    if (lc.includes("%") || lc.includes("taux") || lc.includes("pct")) return `${Number(val).toFixed(2).replace(".",",")}%`;
    if (Number.isInteger(val)) return fmtInt(val);
    return fmtNum(val, 2);
  }
  return String(val);
}

/* ─── Drawer détail produits d'un corner (Écarts) ─── */
function EcartCornerDrawer({ corner_key, filters, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!corner_key) return;
    const qp = toQP(filters, { corner_key });
    api.get(`/kpi/inventaire/ecart/corner-products?${qp}`)
       .then(r => setData(r.data))
       .catch(()=>setData({rows:[], total:{}, corner:null}));
  }, [corner_key, filters]);
  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose}/>
      <div className="fixed right-0 top-0 h-full w-[720px] max-w-full bg-white z-50 shadow-2xl overflow-y-auto p-5" data-testid="ecart-drawer">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Détail écarts produits</div>
            <div className="text-lg font-bold text-slate-900">{data?.corner?.corner || corner_key}</div>
            <div className="text-[11px] text-slate-500">
              {data?.corner?.dr && `DR ${data.corner.dr}`}
              {data?.corner?.secteur && ` · ${data.corner.secteur}`}
              {data?.corner?.enseigne && ` · ${data.corner.enseigne}`}
            </div>
          </div>
          <button onClick={onClose} data-testid="ecart-drawer-close" className="p-1 rounded hover:bg-slate-100"><XIcon className="w-5 h-5"/></button>
        </div>
        {!data && <div className="text-sm text-slate-400 italic">Chargement…</div>}
        {data && data.rows && data.rows.length === 0 && <div className="text-sm text-slate-400 italic py-6 text-center">Aucun produit inventorié pour ce corner avec ces filtres.</div>}
        {data && data.rows && data.rows.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
              {data.rows.length} produits · classés par écart € croissant (plus grosse perte en tête)
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="min-w-[680px] w-full text-[12px]">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Produit</th>
                    <th className="px-3 py-2 text-right">Stock théo. Qté</th>
                    <th className="px-3 py-2 text-right">Stock compté Qté</th>
                    <th className="px-3 py-2 text-right">Écart Qté</th>
                    <th className="px-3 py-2 text-right">Prix U.</th>
                    <th className="px-3 py-2 text-right">Écart €</th>
                    <th className="px-3 py-2 text-right">Écart %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => {
                    const pct = r.ecart_pct || 0;
                    const cls = Math.abs(pct) > 0.05 ? "bg-rose-50 text-rose-700" : Math.abs(pct) > 0.02 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
                    const negTone = (r.ecart_eur || 0) < 0 ? "text-rose-600" : (r.ecart_eur||0) > 0 ? "text-emerald-600" : "text-slate-500";
                    return (
                      <tr key={i} data-testid={`ecart-drawer-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-semibold text-slate-900">{r.produit}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.stock_theorique_qte,2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.stock_compte_qte,2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtNum(r.ecart_qte,2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.prix_unitaire==null?"—":fmtEur(r.prix_unitaire)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${negTone}`}>{fmtEur(r.ecart_eur)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold rounded ${cls}`}>{fmtPct(pct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr className="text-[12px] font-bold text-slate-900">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(data.total?.stock_theorique_qte||0,2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(data.total?.stock_compte_qte||0,2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(data.total?.ecart_qte||0,2)}</td>
                    <td className="px-3 py-2"></td>
                    <td className={`px-3 py-2 text-right tabular-nums ${(data.total?.ecart_eur||0)<0?"text-rose-600":"text-emerald-600"}`}>{fmtEur(data.total?.ecart_eur||0)}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ─── Onglet générique data-driven ─── */
function GenericInventoryTab({ coll, testidPrefix, showProduit, cardsRenderer, colorizeRow, onCornerClick }) {
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ drs:[], secteurs:[], enseignes:[], corners:[], produits:[], mois:[], annees:[] });
  const [view, setView] = useState("corner");
  const [data, setData] = useState({ columns:[], numeric_columns:[], rows:[], totals:{cells:{}}, cards:{} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/kpi/inventaire/meta?coll=${coll}`)
       .then(r => setMeta(r.data))
       .catch(()=>setMeta({}));
  }, [coll]);

  const reload = useCallback(() => {
    setLoading(true);
    const qp = toQP(f, { coll, view });
    api.get(`/kpi/inventaire/table?${qp}`)
       .then(r => setData(r.data))
       .catch(()=>setData({columns:[],numeric_columns:[],rows:[],totals:{cells:{}},cards:{}}))
       .finally(()=>setLoading(false));
  }, [f, view, coll]);
  useEffect(() => { reload(); }, [reload]);

  const numericSet = useMemo(()=>new Set(data.numeric_columns||[]), [data.numeric_columns]);
  const displayCols = useMemo(() => (data.columns||[]).slice(), [data.columns]);
  const totalsRow = data.totals?.cells || {};

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-tab`}>
      <TabFilterBar meta={meta} f={f} setF={setF} showProduit={showProduit} testidPrefix={testidPrefix}/>
      {cardsRenderer && cardsRenderer(data.cards || {})}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <ViewToggle mode={view} setMode={setView} testid={`${testidPrefix}-view`}/>
          <span className="text-[11px] text-slate-500 ml-auto">
            {loading ? "Chargement…" : `${(data.rows||[]).length} lignes · ${displayCols.length} colonnes du fichier importé`}
            {onCornerClick && view === "corner" ? " · clique une ligne pour ouvrir le détail" : ""}
          </span>
        </div>
        <div className="overflow-x-auto max-h-[620px] scrollbar-thin">
          <table className="min-w-[1100px] w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2 min-w-[200px] sticky left-0 bg-slate-50 z-20">
                  {view === "corner" ? "Corner" : view === "dr" ? "DR" : "Secteur"}
                </th>
                {displayCols.map(c => (
                  <th key={c} className={`px-3 py-2 min-w-[120px] ${numericSet.has(c) ? "text-right" : ""}`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.rows||[]).map((r, idx) => {
                const rowCls = colorizeRow ? colorizeRow(r) : "";
                const clickable = onCornerClick && view === "corner" && r.corner_key;
                return (
                  <tr key={r._key + idx}
                      data-testid={`${testidPrefix}-row-${idx}`}
                      onClick={()=>clickable && onCornerClick(r.corner_key, r.corner)}
                      className={`border-b border-slate-100 ${rowCls} ${clickable?"hover:bg-blue-100/60 cursor-pointer":"hover:bg-blue-50/30"}`}>
                    <td className="px-3 py-2 font-semibold text-slate-900 sticky left-0 bg-white z-10">
                      {r._display_label || r.corner || "—"}
                      {r._count_corners > 1 && (
                        <span className="ml-1 text-[10px] font-normal text-slate-500">({r._count_corners} corners)</span>
                      )}
                    </td>
                    {displayCols.map(c => {
                      const isN = numericSet.has(c);
                      const cellVal = (r.cells || {})[c];
                      return (
                        <td key={c} className={`px-3 py-2 tabular-nums ${isN ? "text-right" : ""}`}>
                          {renderCell(c, cellVal, isN)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {(data.rows||[]).length === 0 && (
                <tr>
                  <td colSpan={displayCols.length + 1} className="px-4 py-8 text-center text-slate-400 italic">
                    Aucune donnée. Importe le fichier via <b>Administration → Centre d'Import → Import Inventaire</b>.
                  </td>
                </tr>
              )}
            </tbody>
            {(data.rows||[]).length > 0 && (
              <tfoot className="bg-slate-100 border-t-2 border-slate-300 sticky bottom-0">
                <tr className="text-[12px] font-bold text-slate-900">
                  <td className="px-3 py-2 sticky left-0 bg-slate-100 z-10" data-testid={`${testidPrefix}-total-label`}>TOTAL</td>
                  {displayCols.map(c => {
                    const isN = numericSet.has(c);
                    const v = isN ? totalsRow[c] : "";
                    return (
                      <td key={c} className={`px-3 py-2 tabular-nums ${isN ? "text-right" : ""}`}>
                        {isN ? renderCell(c, v, true) : ""}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══ ONGLET ÉCARTS (avec drawer) ═══ */
function TabEcarts() {
  const [drawerCk, setDrawerCk] = useState(null);
  const [drawerFilters, setDrawerFilters] = useState({});
  // On duplique la logique GenericInventoryTab pour capter les filtres localement et les passer au drawer.
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ drs:[], secteurs:[], enseignes:[], corners:[], produits:[], mois:[], annees:[] });
  const [view, setView] = useState("corner");
  const [data, setData] = useState({ columns:[], numeric_columns:[], rows:[], totals:{cells:{}}, cards:{} });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get(`/kpi/inventaire/meta?coll=kpi_ecart`).then(r => setMeta(r.data)).catch(()=>setMeta({})); }, []);
  const reload = useCallback(() => {
    setLoading(true);
    const qp = toQP(f, { coll: "kpi_ecart", view });
    api.get(`/kpi/inventaire/table?${qp}`)
       .then(r => setData(r.data))
       .catch(()=>setData({columns:[],numeric_columns:[],rows:[],totals:{cells:{}},cards:{}}))
       .finally(()=>setLoading(false));
  }, [f, view]);
  useEffect(() => { reload(); }, [reload]);

  const numericSet = useMemo(()=>new Set(data.numeric_columns||[]), [data.numeric_columns]);
  const displayCols = useMemo(() => (data.columns||[]).slice(), [data.columns]);
  const totalsRow = data.totals?.cells || {};

  const cards = data.cards || {};
  const deltaEur = cards.delta_eur_vs_prev;
  const deltaPct = cards.delta_pct_vs_prev;
  const deltaTone = deltaEur == null ? "" : (deltaEur > 0 ? "down" : "up");
  const colorizeRow = (r) => {
    const pct = Math.abs(r._ecart_pct || 0);
    if (pct > 0.05) return "bg-rose-50/60";
    if (pct > 0.02) return "bg-amber-50/50";
    if (pct > 0)    return "bg-emerald-50/40";
    return "";
  };

  const openDrawer = (corner_key) => {
    setDrawerFilters({ ...f });
    setDrawerCk(corner_key);
  };

  return (
    <div className="space-y-3" data-testid="ecart-tab">
      <TabFilterBar meta={meta} f={f} setF={setF} showProduit={true} testidPrefix="ecart"/>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card testid="ecart-card-total" icon={AlertTriangle} label="Total écart de stock (€)"
              value={fmtEur(cards.ecart_total_eur)}
              tone={(cards.ecart_total_eur||0) === 0 ? "" : (cards.ecart_total_eur||0) < 0 ? "down" : "up"}/>
        <Card testid="ecart-card-anomalies" icon={AlertTriangle}
              label="Corners en anomalie (> 5%)"
              value={fmtInt(cards.n_corners_anomalie||0)}
              alert={(cards.n_corners_anomalie||0) > 0}/>
        <Card testid="ecart-card-evol" icon={deltaEur != null && deltaEur > 0 ? TrendingUp : TrendingDown}
              label="Évolution vs M-1"
              value={deltaEur == null ? "—" : (deltaEur >= 0 ? "+" : "") + fmtEur(deltaEur)}
              sub={deltaPct == null ? "Sélectionne 1 mois + 1 année" : `${deltaPct >= 0 ? "+" : ""}${(deltaPct*100).toFixed(1).replace(".",",")}%`}
              tone={deltaTone}/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <ViewToggle mode={view} setMode={setView} testid="ecart-view"/>
          <span className="text-[11px] text-slate-500 ml-auto">
            {loading ? "Chargement…" : `${(data.rows||[]).length} lignes · ${displayCols.length} colonnes`}
            {view === "corner" ? " · clique une ligne pour ouvrir le détail produits" : ""}
          </span>
        </div>
        <div className="overflow-x-auto max-h-[620px] scrollbar-thin">
          <table className="min-w-[1100px] w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-left text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
                <th className="px-3 py-2 min-w-[200px] sticky left-0 bg-slate-50 z-20">
                  {view === "corner" ? "Corner" : view === "dr" ? "DR" : "Secteur"}
                </th>
                {displayCols.map(c => (
                  <th key={c} className={`px-3 py-2 min-w-[120px] ${numericSet.has(c) ? "text-right" : ""}`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.rows||[]).map((r, idx) => {
                const rowCls = colorizeRow(r);
                const clickable = view === "corner" && r.corner_key;
                return (
                  <tr key={r._key + idx} data-testid={`ecart-row-${idx}`}
                      onClick={()=>clickable && openDrawer(r.corner_key)}
                      className={`border-b border-slate-100 ${rowCls} ${clickable?"hover:bg-blue-100/60 cursor-pointer":"hover:bg-blue-50/30"}`}>
                    <td className="px-3 py-2 font-semibold text-slate-900 sticky left-0 bg-white z-10">
                      {r._display_label || r.corner || "—"}
                      {r._count_corners > 1 && <span className="ml-1 text-[10px] font-normal text-slate-500">({r._count_corners} corners)</span>}
                    </td>
                    {displayCols.map(c => {
                      const isN = numericSet.has(c);
                      const cellVal = (r.cells || {})[c];
                      return <td key={c} className={`px-3 py-2 tabular-nums ${isN?"text-right":""}`}>{renderCell(c, cellVal, isN)}</td>;
                    })}
                  </tr>
                );
              })}
              {(data.rows||[]).length === 0 && (
                <tr><td colSpan={displayCols.length + 1} className="px-4 py-8 text-center text-slate-400 italic">
                  Aucune donnée. Importe le fichier via <b>Administration → Centre d'Import → Import Inventaire</b>.
                </td></tr>
              )}
            </tbody>
            {(data.rows||[]).length > 0 && (
              <tfoot className="bg-slate-100 border-t-2 border-slate-300 sticky bottom-0">
                <tr className="text-[12px] font-bold text-slate-900">
                  <td className="px-3 py-2 sticky left-0 bg-slate-100 z-10" data-testid="ecart-total-label">TOTAL</td>
                  {displayCols.map(c => {
                    const isN = numericSet.has(c);
                    const v = isN ? totalsRow[c] : "";
                    return <td key={c} className={`px-3 py-2 tabular-nums ${isN?"text-right":""}`}>{isN ? renderCell(c, v, true) : ""}</td>;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {drawerCk && <EcartCornerDrawer corner_key={drawerCk} filters={drawerFilters} onClose={()=>setDrawerCk(null)}/>}
    </div>
  );
}

/* ═══ ONGLET INVENTAIRE ═══ */
function TabInventaire() {
  const cardsRenderer = (cards) => {
    const deltaEur = cards.delta_eur_vs_prev;
    const deltaPct = cards.delta_pct_vs_prev;
    const deltaTone = deltaEur == null ? "" : (deltaEur > 0 ? "down" : "up");
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card testid="inv-card-theo" icon={Boxes} label="Stock théorique total" value={fmtEur(cards.stock_theorique_total)}/>
        <Card testid="inv-card-reel" icon={Warehouse} label="Stock compté total" value={fmtEur(cards.stock_reel_total)}/>
        <Card testid="inv-card-ecart" icon={AlertTriangle} label="Écart global"
              value={fmtEur(cards.ecart_global)}
              tone={(cards.ecart_global||0) === 0 ? "" : (cards.ecart_global||0) < 0 ? "down" : "up"}/>
        <Card testid="inv-card-evol" icon={deltaEur != null && deltaEur > 0 ? TrendingUp : TrendingDown}
              label="Évolution vs M-1"
              value={deltaEur == null ? "—" : (deltaEur >= 0 ? "+" : "") + fmtEur(deltaEur)}
              sub={deltaPct == null ? "Sélectionne 1 mois + 1 année" : `${deltaPct >= 0 ? "+" : ""}${(deltaPct*100).toFixed(1).replace(".",",")}%`}
              tone={deltaTone}/>
      </div>
    );
  };
  return <GenericInventoryTab coll="kpi_inventaire" testidPrefix="inv" showProduit={false} cardsRenderer={cardsRenderer}/>;
}

/* ═══ ONGLET DÉMARQUE ═══ */
function TabDemarque() {
  const cardsRenderer = (cards) => {
    const alert = !!cards.alert;
    const deltaEur = cards.delta_eur_vs_prev;
    const deltaPct = cards.delta_pct_vs_prev;
    const deltaTone = deltaEur == null ? "" : (deltaEur > 0 ? "down" : "up");
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card testid="dem-card-connue" icon={Package} label="Démarque connue €" value={fmtEur(cards.demarque_connue_total)}/>
        <Card testid="dem-card-inconnue" icon={AlertTriangle} label="Démarque inconnue €" value={fmtEur(cards.demarque_inconnue_total)} alert={alert}/>
        <Card testid="dem-card-taux" icon={AlertTriangle}
              label={`Taux démarque inconnue (seuil ${fmtPct(cards.threshold||0.005,1)})`}
              value={fmtPct(cards.taux_demarque_inconnue, 2)}
              sub={alert ? "⚠ Au-dessus du seuil" : "Sous le seuil"}
              alert={alert}/>
        <Card testid="dem-card-evol" icon={deltaEur != null && deltaEur > 0 ? TrendingUp : TrendingDown}
              label="Évolution vs M-1"
              value={deltaEur == null ? "—" : (deltaEur >= 0 ? "+" : "") + fmtEur(deltaEur)}
              sub={deltaPct == null ? "Sélectionne 1 mois + 1 année" : `${deltaPct >= 0 ? "+" : ""}${(deltaPct*100).toFixed(1).replace(".",",")}%`}
              tone={deltaTone}/>
      </div>
    );
  };
  return <GenericInventoryTab coll="kpi_demarque" testidPrefix="dem" showProduit={true} cardsRenderer={cardsRenderer}/>;
}

/* ═══ COMPOSANT PRINCIPAL ═══ */
function InpulseStockTab() {
  const now = new Date(); const [key, setKey] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`); const [d, setD] = useState(null);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => { const dt = new Date(now.getFullYear(), now.getMonth() - i, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`; }), []); // eslint-disable-line
  useEffect(() => { setD(null); api.post("/produits-analytics/achats-inpulse", { period: { kind: "month", key }, filters: {}, compare: "n1" }).then(r => setD(r.data)).catch(() => setD({ state: "INDISPONIBLE" })); }, [key]);
  return (
    <div className="space-y-3" data-testid="inv-inpulse-tab">
      <div className="flex items-center gap-2 text-[11px]"><span className="font-semibold text-slate-500">Période</span><select value={key} onChange={e => setKey(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1 bg-white" data-testid="inv-inpulse-period">{months.map(m => <option key={m} value={m}>{m}</option>)}</select><span className="text-slate-400">Stocks, inventaires et écarts officiels Inpulse (past-stocks) — réseau complet ; l'analyse par DR / secteur / magasin est dans KPI Achats › Analyse Inpulse.</span></div>
      {d ? <InventaireInpulse d={d} /> : <div className="text-[12px] text-slate-500">Lecture des stocks Inpulse…</div>}
    </div>
  );
}

export default function KpiAchatsInventaires() {
  const [subTab, setSubTab] = useState("ecarts");

  const tabs = [
    { k:"ecarts",     label:"Écarts",      icon:AlertTriangle },
    { k:"inventaire", label:"Inventaire",  icon:Boxes },
    { k:"demarque",   label:"Démarque",    icon:Package },
    { k:"inpulse",    label:"Stock Inpulse", icon:Boxes },
  ];

  return (
    <div className="space-y-3" data-testid="inv-tab-root">
      <div className="flex items-center gap-2 border-b border-slate-200">
        {tabs.map(t => {
          const Icon = t.icon; const active = subTab===t.k;
          return (
            <button key={t.k} data-testid={`inv-subtab-${t.k}`} onClick={()=>setSubTab(t.k)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold border-b-2 transition ${active?"border-blue-600 text-blue-600":"border-transparent text-slate-500 hover:text-slate-700"}`}>
              <Icon className="w-3.5 h-3.5"/>
              {t.label}
            </button>
          );
        })}
      </div>
      {subTab==="ecarts"     && <TabEcartsPro/>}
      {subTab==="inventaire" && <TabInventaire/>}
      {subTab==="demarque"   && <DemarqueTab/>}
      {subTab==="inpulse"    && <InpulseStockTab/>}
    </div>
  );
}
